/**
 * Defines API endpoint routes used by the authentication module.
 * These routes are used for authentication-related backend operations
 * such as signing in, refreshing authentication tokens, password recovery,
 * token verification, password reset, and retrieving user permissions.
 */
export const AUTH_API_ROUTES = {
  /**
   * API endpoint used to authenticate a user with their credentials.
   */
  SIGN_IN: '/v1/auth/signin',

  /**
   * API endpoint used to refresh an expired or expiring authentication token.
   */
  REFRESH_TOKEN: '/v1/auth/refresh-token',

  /**
   * API endpoint used to request a password reset link.
   */
  FORGOT_PASSWORD: '/v1/auth/forgot-password',

  /**
   * API endpoint used to verify a password reset or authentication token.
   */
  VERIFY_TOKEN: '/v1/auth/verify-token',

  /**
   * API endpoint used to reset the user's password.
   */
  RESET_PASSWORD: '/v1/auth/reset-password',

  /**
   * API endpoint used to retrieve the authenticated user's
   * roles and permissions.
   */
  USER_ROLE_PERMISSION: '/v1/auth/user-role-permission',
} as const;

/**
 * Defines application routes used by the authentication module.
 * These routes are used for navigation between authentication pages
 * and the application's main dashboard.
 */
export const AUTH_APP_ROUTES = {
  /**
   * Route for the sign-in page.
   */
  SIGNIN: '/signin',

  /**
   * Route for the forgot-password page.
   */
  FORGOT_PASSWORD: '/forgotPassword',

  /**
   * Route for the reset-password page.
   */
  RESET_PASSWORD: '/resetPassword',

  /**
   * Route for the main analytics dashboard.
   */
  DASHBOARD: '/analytics/signup',
} as const;

/**
 * Defines static content displayed in the authentication page banner.
 * Contains branding, portal information, hero content, and
 * security-related footer text.
 */
export const AUTH_BANNER_CONTENT = {
  /**
   * Brand name displayed in the authentication banner.
   */
  BRAND_NAME: 'ZenBasket',

  /**
   * Main title of the internal portal.
   */
  PORTAL_TITLE: 'Master Dashboard',

  /**
   * Subtitle describing the portal.
   */
  PORTAL_SUBTITLE: 'Analytics Portal',

  /**
   * Main heading displayed in the authentication hero section.
   */
  HERO_HEADING: 'ZenBasket Master!',

  /**
   * Description displayed below the hero heading.
   */
  HERO_DESCRIPTION:
    'Welcome to your Store Admin Dashboard. Manage your inventory, oversee orders, and optimize your store operations with ease.',

  /**
   * Security-related text displayed in the authentication footer.
   */
  FOOTER_SECURITY_TEXT: 'Secure Internal Portal',
} as const;

/**
 * Defines the static content used by the sign-in page.
 * Includes form labels, placeholders, navigation text, button text,
 * and validation or API error messages.
 */
export const SIGNIN_CONTENT = {
  /**
   * Title displayed on the sign-in page.
   */
  TITLE: 'Welcome Back!',

  /**
   * Supporting text displayed below the sign-in title.
   */
  SUBTITLE: 'Sign in to access the Master Dashboard',

  /**
   * Label displayed for the email input field.
   */
  EMAIL_LABEL: 'Email Address',

  /**
   * Placeholder displayed in the email input field.
   */
  EMAIL_PLACEHOLDER: 'Enter your email address',

  /**
   * Label displayed for the password input field.
   */
  PASSWORD_LABEL: 'Password',

  /**
   * Placeholder displayed in the password input field.
   */
  PASSWORD_PLACEHOLDER: 'Enter your password',

  /**
   * Text for remember me checkbox.
   */
  REMEMBER_ME: 'Remember me',

  /**
   * Text displayed for the forgot-password navigation link.
   */
  FORGOT_PASSWORD_LINK: 'Forgot Password?',

  /**
   * Label displayed on the sign-in submit button.
   */
  SUBMIT_BUTTON: 'Sign In',

  /**
   * Prompt text for sign-up link.
   */
  SIGNUP_PROMPT: 'Have an Account?',

  /**
   * Label for sign-up link.
   */
  SIGNUP_LINK: 'Sign up',

  /**
   * Validation and error messages displayed by the sign-in form.
   */
  ERRORS: {
    /**
     * Message displayed when the email field is empty.
     */
    EMAIL_REQUIRED: 'Enter a valid email address',

    /**
     * Message displayed when the email format is invalid.
     */
    EMAIL_INVALID: 'Enter a valid email address',

    /**
     * Message displayed when the password field is empty.
     */
    PASSWORD_REQUIRED: 'Password is required',

    /**
     * Message displayed when the supplied credentials are invalid.
     */
    INVALID_CREDENTIALS:
      'Invalid login credential, please try again',

    /**
     * Generic message displayed when the sign-in request fails
     * for an unexpected reason.
     */
    GENERIC:
      'Invalid login credential, please try again',
  },
} as const;

/**
 * Defines the static content used by the forgot-password page.
 * Includes navigation text, form labels, placeholders, success messages,
 * and validation or API error messages.
 */
export const FORGOT_PASSWORD_CONTENT = {
  /**
   * Text displayed for navigating back to the sign-in page.
   */
  BACK_LINK: 'Back to Sign in',

  /**
   * Title displayed on the forgot-password page.
   */
  TITLE: 'Forgot Password?',

  /**
   * Supporting description displayed below the page title.
   */
  SUBTITLE:
    "No worries! Enter your email address and we'll send you a link to reset your password.",

  /**
   * Label displayed for the email input field.
   */
  EMAIL_LABEL: 'Email Address',

  /**
   * Placeholder displayed in the email input field.
   */
  EMAIL_PLACEHOLDER: 'Enter your email address',

  /**
   * Label displayed on the password reset request button.
   */
  SUBMIT_BUTTON: 'Send Reset Link',

  /**
   * Prompt displayed for users who remember their password.
   */
  REMEMBER_PROMPT: 'Remember your password?',

  /**
   * Text displayed for navigating back to the sign-in page.
   */
  SIGNIN_LINK: 'Sign in',

  /**
   * Message displayed after a password reset link is successfully sent.
   */
  SUCCESS_MESSAGE:
    'Password reset link has been sent to your email address. Redirecting to sign in...',

  /**
   * Validation and API error messages used by the forgot-password form.
   */
  ERRORS: {
    /**
     * Message displayed when the email field is empty.
     */
    EMAIL_REQUIRED: 'Email address is required.',

    /**
     * Message displayed when the email format is invalid.
     */
    EMAIL_INVALID: 'Please enter a valid email address.',

    /**
     * Generic message displayed when the reset-link request fails.
     */
    GENERIC:
      'Unable to send reset link. Please try again later.',
  },
} as const;

/**
 * Defines the static content used by the reset-password page.
 * Includes navigation text, password form labels, password requirements,
 * success messages, and validation or API error messages.
 */
export const RESET_PASSWORD_CONTENT = {
  /**
   * Text displayed for navigating back to the sign-in page.
   */
  BACK_LINK: 'Back to Sign in',

  /**
   * Title displayed on the reset-password page.
   */
  TITLE: 'Reset Password',

  /**
   * Supporting description displayed below the page title.
   */
  SUBTITLE:
    "Securely update your password in a few steps. Make sure it's strong and memorable.",

  /**
   * Label displayed for the new password field.
   */
  NEW_PASSWORD_LABEL: 'New Password',

  /**
   * Placeholder displayed in the new password field.
   */
  NEW_PASSWORD_PLACEHOLDER: 'Enter your new password',

  /**
   * Password requirements displayed to the user.
   */
  PASSWORD_HINT:
    'Min 8 characters with uppercase, lowercase, number & special',

  /**
   * Label displayed for the password confirmation field.
   */
  CONFIRM_PASSWORD_LABEL: 'Confirm Password',

  /**
   * Placeholder displayed in the password confirmation field.
   */
  CONFIRM_PASSWORD_PLACEHOLDER: 'Confirm your new password',

  /**
   * Label displayed on the reset-password submit button.
   */
  SUBMIT_BUTTON: 'Reset Password',

  /**
   * Message displayed when the new password and confirmation password
   * do not match.
   */
  PASSWORD_MISMATCH: 'Passwords do not match',

  /**
   * Message displayed after the password is successfully reset.
   */
  SUCCESS_MESSAGE:
    'Your password has been reset successfully. Redirecting to sign in...',

  /**
   * Validation and API error messages used by the reset-password form.
   */
  ERRORS: {
    /**
     * Message displayed when the new password field is empty.
     */
    PASSWORD_REQUIRED: 'New password is required.',

    /**
     * Message displayed when the password does not meet
     * the required complexity rules.
     */
    PASSWORD_PATTERN:
      'Must be at least 8 characters with uppercase, lowercase, number & special character.',

    /**
     * Message displayed when the password confirmation field is empty.
     */
    CONFIRM_PASSWORD_REQUIRED:
      'Please confirm your password.',

    /**
     * Message displayed when the passwords do not match.
     */
    PASSWORDS_DONT_MATCH: 'Passwords do not match',

    /**
     * Generic message displayed when the password reset request fails.
     */
    GENERIC:
      'Unable to reset password. Please try again later.',
  },
} as const;

/**
 * Defines the static content used by the set-password page.
 * Includes navigation text, password form labels, password requirements,
 * success messages, and validation or API error messages.
 */
export const SET_PASSWORD_CONTENT = {
  /**
   * Text displayed for navigating back to the sign-in page.
   */
  BACK_LINK: 'Back to Sign in',

  /**
   * Title displayed on the set-password page.
   */
  TITLE: 'Set Password',

  /**
   * Supporting description displayed below the page title.
   */
  SUBTITLE:
    'Create a strong and secure password for your account to get started.',

  /**
   * Label displayed for the password field.
   */
  NEW_PASSWORD_LABEL: 'Password',

  /**
   * Placeholder displayed in the password field.
   */
  NEW_PASSWORD_PLACEHOLDER: 'Enter your password',

  /**
   * Password requirements displayed to the user.
   */
  PASSWORD_HINT:
    'Min 8 characters with uppercase, lowercase, number & special',

  /**
   * Label displayed for the password confirmation field.
   */
  CONFIRM_PASSWORD_LABEL: 'Confirm Password',

  /**
   * Placeholder displayed in the password confirmation field.
   */
  CONFIRM_PASSWORD_PLACEHOLDER: 'Confirm your password',

  /**
   * Label displayed on the set-password submit button.
   */
  SUBMIT_BUTTON: 'Set Password',

  /**
   * Message displayed when the password and confirmation password
   * do not match.
   */
  PASSWORD_MISMATCH: 'Passwords do not match',

  /**
   * Message displayed after the password is successfully set.
   */
  SUCCESS_MESSAGE:
    'Your password has been set successfully. Redirecting to sign in...',

  /**
   * Validation and API error messages used by the set-password form.
   */
  ERRORS: {
    /**
     * Message displayed when the password field is empty.
     */
    PASSWORD_REQUIRED: 'Password is required.',

    /**
     * Message displayed when the password does not meet
     * the required complexity rules.
     */
    PASSWORD_PATTERN:
      'Must be at least 8 characters with uppercase, lowercase, number & special character.',

    /**
     * Message displayed when the password confirmation field is empty.
     */
    CONFIRM_PASSWORD_REQUIRED:
      'Please confirm your password.',

    /**
     * Message displayed when the passwords do not match.
     */
    PASSWORDS_DONT_MATCH: 'Passwords do not match',

    /**
     * Generic message displayed when the password setup request fails.
     */
    GENERIC:
      'Unable to set password. Please try again later.',
  },
} as const;